import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatMoney } from '@lombok/shared';
import type { Customer } from '@lombok/shared';
import { Button, Card } from '@lombok/ui';
import { PageHeader } from '@/components/PageHeader';
import { SearchInput } from '@/components/SearchInput';
import { StatusPill, statusLabel } from '@/components/StatusPill';
import { QueryState } from '@/components/QueryState';
import { useAuth } from '@/auth/AuthContext';
import { canCreateCustomer } from './create';
import { useCustomers } from '@/data/hooks';
import { TERMS_LABELS_ES } from '@/features/admin/labels';
import { SortSelect } from '@/components/SortSelect';
import { byDate, byNumber, byText, sortItems, thenBy, type SortOption } from '@/lib/sort';

const CUSTOMER_SORT: SortOption<Customer>[] = [
  { value: 'recent', label: 'Más recientes', compare: byDate((c) => c.createdAt, 'desc') },
  { value: 'az', label: 'Nombre A–Z', compare: byText((c) => c.name) },
  { value: 'za', label: 'Nombre Z–A', compare: byText((c) => c.name, 'desc') },
  { value: 'balance_desc', label: 'Saldo mayor', compare: byNumber((c) => c.openBalanceMinorUsd, 'desc') },
  {
    value: 'status',
    label: 'Estado',
    compare: thenBy<Customer>(
      (a, b) => statusLabel('account', a.status).localeCompare(statusLabel('account', b.status), 'es'),
      byText((c) => c.name),
    ),
  },
];

/**
 * `basePath` lets this list be reused across role groups (admin/sales). Row
 * links and the "Nuevo cliente" action resolve against it; in the admin group
 * they point at the customer create/detail screens.
 */
interface CustomersListPageProps {
  basePath?: string;
  title?: string;
  description?: string;
  emptyTitle?: string;
  emptyDescription?: string;
}

/**
 * Clientes list. Fetches via the typed API client + TanStack Query. Surfaces
 * account status (pill), payment terms, and — prominently — open balance and
 * available credit (a docs/11 priority). All money via the shared formatMoney
 * helper (minor units, es-VE locale).
 */
export function CustomersListPage({
  basePath,
  title = 'Clientes',
  description = 'Cuentas, crédito y saldo.',
  emptyTitle = 'No hay clientes',
  emptyDescription = 'Aún no se han registrado clientes.',
}: CustomersListPageProps = {}) {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('recent');
  const { session } = useAuth();
  const canCreate = canCreateCustomer(session?.user.role);
  const query = useCustomers({ limit: 100 });

  const items = query.data?.items ?? [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortItems(items, CUSTOMER_SORT, sort);
    return sortItems(
      items.filter(
        (c) => c.name.toLowerCase().includes(q) || c.rif.toLowerCase().includes(q),
      ),
      CUSTOMER_SORT,
      sort,
    );
  }, [items, search, sort]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={title}
        description={description}
        actions={
          basePath && canCreate ? (
            <Link to={`${basePath}/nuevo`}>
              <Button size="sm">Nuevo cliente</Button>
            </Link>
          ) : undefined
        }
      />

      <Card className="overflow-hidden">
        <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-center">
          <div className="flex-1">
            <SearchInput value={search} onChange={setSearch} placeholder="Buscar por nombre o RIF…" />
          </div>
          <SortSelect options={CUSTOMER_SORT} value={sort} onChange={setSort} />
        </div>

        <QueryState
          isLoading={query.isLoading}
          isError={query.isError}
          error={query.error}
          onRetry={() => void query.refetch()}
          isEmpty={filtered.length === 0}
          loadingLabel="Cargando clientes…"
          loadingVariant="table"
          skeletonCols={4}
          emptyTitle={search ? 'Sin coincidencias' : emptyTitle}
          emptyDescription={
            search
              ? 'Ajusta la búsqueda para ver más resultados.'
              : emptyDescription
          }
        >
          {/* Desktop table */}
          <div className="hidden md:block">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wide text-text-muted">
                  <th className="px-4 py-3 font-medium">Cliente</th>
                  <th className="px-4 py-3 font-medium">RIF</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                  <th className="px-4 py-3 font-medium">Términos</th>
                  <th className="px-4 py-3 text-right font-medium">Saldo pendiente</th>
                  <th className="px-4 py-3 text-right font-medium">Crédito disponible</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.customerId} className="border-b border-border last:border-0 hover:bg-surface-muted">
                    <td className="px-4 py-3 font-medium text-text-primary">
                      {basePath ? (
                        <Link
                          to={`${basePath}/${c.customerId}`}
                          className="text-brand-primary hover:underline"
                        >
                          {c.name}
                        </Link>
                      ) : (
                        c.name
                      )}
                    </td>
                    <td className="px-4 py-3 text-text-muted">{c.rif}</td>
                    <td className="px-4 py-3">
                      <StatusPill kind="account" value={c.status} />
                    </td>
                    <td className="px-4 py-3 text-text-muted">{TERMS_LABELS_ES[c.paymentTerms]}</td>
                    <td className="px-4 py-3 text-right font-medium text-text-primary">
                      {formatMoney(c.openBalanceMinorUsd)}
                    </td>
                    <td className="px-4 py-3 text-right text-success">
                      {formatMoney(c.availableCreditMinorUsd)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <ul className="divide-y divide-border md:hidden">
            {filtered.map((c) => (
              <li key={c.customerId} className="p-4">
                {basePath ? (
                  <Link
                    to={`${basePath}/${c.customerId}`}
                    className="-m-4 block p-4 active:bg-surface-muted"
                  >
                    <CustomerCardBody customer={c} />
                  </Link>
                ) : (
                  <CustomerCardBody customer={c} />
                )}
              </li>
            ))}
          </ul>
        </QueryState>
      </Card>
    </div>
  );
}

/** Mobile card body (shared between the linked and plain variants). */
function CustomerCardBody({ customer: c }: { customer: Customer }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-medium text-text-primary">{c.name}</p>
          <p className="text-xs text-text-muted">{c.rif}</p>
        </div>
        <StatusPill kind="account" value={c.status} />
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="rounded-md bg-surface-muted p-2">
          <p className="text-xs text-text-muted">Saldo pendiente</p>
          <p className="font-medium text-text-primary">{formatMoney(c.openBalanceMinorUsd)}</p>
        </div>
        <div className="rounded-md bg-surface-muted p-2">
          <p className="text-xs text-text-muted">Crédito disponible</p>
          <p className="font-medium text-success">{formatMoney(c.availableCreditMinorUsd)}</p>
        </div>
      </div>
      <p className="text-xs text-text-muted">Términos: {TERMS_LABELS_ES[c.paymentTerms]}</p>
    </div>
  );
}
